import { BadgeVariant } from '@fp/ui-kit'

export const statusBadgeMapper: { [key: string]: { [key: string]: BadgeVariant } } = {
  trading_accounts: {
    onboarding: 'primary',
    expired: 'blocked',
    closed: 'secondary',
    new: 'success',
    refunded: 'highlight',
    active: 'success',
    inactive: 'warning',
    ongoing: 'info',
    not_passed: 'error',
    payout_only: 'highlight',
    passed: 'fresh',
    in_review: 'processing',
  },

  deposits: {
    open: 'primary',
    paid: 'success',
    failed: 'error',
    refunded: 'archived',
    expired: 'blocked',
    returned: 'archived',
    cancelled: 'warning',
  },

  transactions: {
    declined: 'error',
    failed: 'error',
    invitation_sent: 'info',
    open: 'primary',
    paid: 'success',
    pending: 'warning',
    partially_processed: 'processing',
    processed: 'success',
    processing: 'processing',
    queued: 'info',
    requested: 'warning',
    returned: 'archived',
    risk_assessment: 'highlight',
    refunded: 'archived',
    expired: 'blocked',
    // BalanceTransaction ledger statuses
    hold: 'warning',
    completed: 'success',
    cancelled: 'secondary',
    // Nested statuses surfaced by the balance transaction detail sheet
    replaced: 'archived',
    refund_declined: 'error',
    partially_refunded: 'processing',
    awaiting_approval: 'warning',
    auto_approved: 'success',
    ended: 'archived',
  },

  // Ledger entry/source types must not go through getVariantForValue: a hashed
  // colour makes credits and debits semantically arbitrary and lets values
  // collide with status colours.
  balance_transaction_entry_types: {
    deposit: 'success',
    purchase: 'primary',
    payout: 'info',
    refund: 'archived',
    adjustment: 'warning',
    internal_transfer: 'processing',
    company_share: 'highlight',
    service_fee: 'neutral',
    payout_refund: 'blocked',
  },

  // Sits next to the status badge: avoid `warning` (pending) and `secondary` (cancelled).
  internal_transfer_kinds: {
    standard: 'outline',
    adjustment: 'info',
  },

  balance_transaction_source_types: {
    PayoutTransaction: 'info',
    PaymentSession: 'primary',
    InternalTransfer: 'processing',
    Payout: 'highlight',
  },
  acquisition_status: {
    new: 'primary',
    attempted_contact: 'info',
    follow_up_required: 'highlight',
    interested: 'neutral',
    need_to_deposit: 'warning',
    no_answer: 'secondary',
    not_interested: 'secondary',
    invalid_lead: 'error',
    duplicate: 'archived',
    do_not_contact: 'error',
    converted: 'success',
  },

  risk_status: {
    requested: 'neutral',
    monitoring: 'processing',
    risk_assessment: 'highlight',
    banned: 'error',
    waiting_client_response: 'info',
    recheck: 'warning',
    declined: 'error',
    proof_pending: 'info',
  },
  phone_number_verification_status: {
    not_verified: 'warning',
    verified: 'success',
    required: 'neutral',
    in_progress: 'info',
  },

  account_status: {
    closed: 'error',
    ongoing: 'success',
  },
  rise_onboarding_status: {
    uninvited: 'warning',
    invited: 'primary',
    onboarded: 'success',
  },
  kycaid_verification_status: {
    unverified: 'error',
    verified: 'success',
    pending: 'warning',
  },
  payout_type: {
    trading_account: 'primary',
    affiliate: 'success',
    calculation_request: 'error',
  },
  referral_type: {
    trading_account: 'primary',
    bonus: 'success',
    payout: 'error',
    recurrent_purchase: 'info',
  },
  tasks_status: {
    created: 'info',
    processing: 'processing',
    failed: 'warning',
    completed: 'success',
    cancelled: 'error',
  },
  scheduled_actions_status: {
    created: 'info',
    processing: 'processing',
    scheduled: 'primary',
    processed: 'success',
    failed: 'error',
    cancelled: 'warning',
  },
  contract_status: {
    sent: 'info',
    signed: 'success',
    withdrawn: 'processing',
    declined: 'error',
  },
}

// Withdrawal/payout transaction statuses share the `transactions` map, whose
// keys are snake_case. Statuses may arrive spaced/hyphenated ("in review") or
// cased; normalize separators to underscores before lookup.
export const getTransactionStatusVariant = (status?: string): BadgeVariant =>
  statusBadgeMapper.transactions?.[
    status
      ?.trim()
      .replaceAll(/[\s-]+/g, '_')
      .toLowerCase() ?? ''
  ] || 'secondary'

export const getBalanceTransactionEntryTypeVariant = (entryType?: string): BadgeVariant =>
  statusBadgeMapper.balance_transaction_entry_types?.[entryType ?? ''] || 'secondary'

export const getBalanceTransactionSourceTypeVariant = (sourceType?: string): BadgeVariant =>
  statusBadgeMapper.balance_transaction_source_types?.[sourceType ?? ''] || 'secondary'

export const getInternalTransferKindVariant = (kind?: string): BadgeVariant =>
  statusBadgeMapper.internal_transfer_kinds?.[kind ?? ''] || 'outline'

const badgeVariants = {
  primary: 'primary',
  blocked: 'blocked',
  secondary: 'secondary',
  success: 'success',
  archived: 'archived',
  warning: 'warning',
  info: 'info',
  error: 'error',
  highlight: 'highlight',
  fresh: 'fresh',
  processing: 'processing',
  neutral: 'neutral',
  paused: 'paused',
  destructive: 'destructive',
  outline: 'outline',
  ghost: 'ghost',
} as const

function hashStringToNumber(str: string): number {
  let hash = 5381
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i)
  }

  return hash >>> 0
}

export function getVariantForValue(value?: string | null): BadgeVariant {
  if (!value) return 'outline'

  const hash = hashStringToNumber(value)
  const index = hash % (Object.keys(badgeVariants || {}).length || 1)

  return Object.values(badgeVariants || {})[index] ?? 'primary'
}
